import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface CloseJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (closingComments: string, completedDate?: string, closedDate?: string) => void;
  isLoading: boolean;
}

export function CloseJobDialog({ open, onOpenChange, onConfirm, isLoading }: CloseJobDialogProps) {
  const [closingComments, setClosingComments] = useState('');
  const [completedDate, setCompletedDate] = useState('');
  const [closedDate, setClosedDate] = useState(() => {
    // Default closed date to today
    return new Date().toISOString().split('T')[0];
  });

  const handleConfirm = () => {
    if (!closingComments.trim()) {
      alert('Closing comments are required');
      return;
    }

    onConfirm(
      closingComments,
      completedDate || undefined,
      closedDate || undefined
    );

    // Reset form
    setClosingComments('');
    setCompletedDate('');
    setClosedDate(new Date().toISOString().split('T')[0]);
  };

  const handleCancel = () => {
    setClosingComments('');
    setCompletedDate('');
    setClosedDate(new Date().toISOString().split('T')[0]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Close Job</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="closingComments">Closing Comments *</Label>
            <Textarea
              id="closingComments"
              value={closingComments}
              onChange={(e) => setClosingComments(e.target.value)}
              placeholder="Enter reason for closing the job..."
              rows={4}
              autoFocus
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="completedDate">Completed Date (Optional)</Label>
            <Input
              id="completedDate"
              type="date"
              value={completedDate}
              onChange={(e) => setCompletedDate(e.target.value)}
              className="h-7 text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="closedDate">Closed Date</Label>
            <Input
              id="closedDate"
              type="date"
              value={closedDate}
              onChange={(e) => setClosedDate(e.target.value)}
              className="h-7 text-sm"
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="action"
              onClick={handleConfirm}
              disabled={isLoading}
            >
              {isLoading ? 'Closing...' : 'Close Job'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
